import { Button, Col, Input, Modal, Spin } from "antd";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import "./Modals.css";
import { Controller, useForm } from "react-hook-form";
import { FormLabel } from "./FormLabel";
import { CheckOutlined, RedoOutlined } from "@ant-design/icons";
import { getSupaClient } from "../../lib/supabase";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import useRefreshChannelsAndRoles from "./useRefreshChannelsAndRoles";

/**
 * Renders the modal after the Play button is clicked
 */
function TryDraftModal({ onCancel }: { onCancel: () => void }) {
  const { state } = useTriggerEditorStateDispatch();
  const isLoading = Boolean(state.editor.isPersisting);

  return (
    <Modal
      visible
      okButtonProps={{ hidden: true }}
      onCancel={onCancel}
      footer={null}
      cancelButtonProps={{ style: { display: "none" } }}
    >
      <Col
        span={14}
        offset={5}
        className="py-4 space-y-6 modal-button-text modal-title-text"
      >
        <div>Testing in #dots-admins</div>
        <Button
          type="primary"
          block
          onClick={onCancel} //here make showForm to false to close the modal
          className="modal-button modal-button-text modal-button-cancel"
        >
          Cancel
        </Button>
        {isLoading ? (
          <div className="spinner">
            <Spin />
          </div>
        ) : (
          <Button
            disabled={isLoading || !!state.editor.visibleErrorBoxId}
            className="modal-button modal-button-text modal-button-link"
          >
            <a
              href={
                state.editor.latestPersistResponses["try-draft"]?.[
                  "dotsAdminsChannelUrl"
                ]
              }
              target="_blank"
            >
              Click here to go to Discord
            </a>
          </Button>
        )}
      </Col>
    </Modal>
  );
}

/**
 * Renders the settings modal
 */
function SettingsModal({
  onCancel,
  changeTriggerName,
}: {
  onCancel: () => void;
  changeTriggerName: (rootId: string, newName: string) => void;
}) {
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const isLoading = Boolean(state.editor.isPersisting);
  const [savingName, setSavingName] = useState(false);
  const readOnly = !state.trigger.current.isDraft;
  const location = useLocation();
  const rootId = location.pathname.split("/")[2];
  const queryClient = useQueryClient();

  const guild = localStorage.getItem("guild-id");

  const { handleSubmit, control } = useForm({
    defaultValues: { name: state?.trigger?.current?.name || "" },
  });

  // Submit form to update trigger name
  const onSubmit = async (data: { name: string }) => {
    setSavingName(true);
    await changeTriggerName(rootId, data.name);
  };

  const {
    isRefreshLoading: isRefreshButtonClicked,
    refreshError: showErrorUpdateMsg,
    refreshSuccess: showSuccessUpdateMsg,
    refresh,
  } = useRefreshChannelsAndRoles();

  // saving name just disable the field and button initialy, when the actions is persisiting is not necessary anymore since both variables have the same purpose but at different times
  useEffect(() => {
    if (isLoading) {
      setSavingName(false);
    }
  }, [isLoading]);

  return (
    <Modal
      visible
      okButtonProps={{ hidden: true }}
      onCancel={onCancel}
      footer={null}
      cancelButtonProps={{ style: { display: "none" } }}
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{ padding: "0 17%" }}
      >
        <h1 className="text-lg font-semibold mt-6">Settings</h1>
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <FormLabel label="Pull in channels and roles:">
            <Button
              disabled={isLoading || savingName || isRefreshButtonClicked}
              className="w-full mb-2"
              type="dashed"
              onClick={refresh}
            >
              <RedoOutlined /> Click here to refresh
            </Button>
          </FormLabel>
          {showSuccessUpdateMsg && (
            <div className="flex text-xs text-dots-vibrant-orange justify-center items-center">
              <CheckOutlined style={{ margin: "0 5px 10px 0" }} />{" "}
              <p>Ready! Channels and roles were updated.</p>
            </div>
          )}
          {showErrorUpdateMsg && (
            <div className="flex text-xs text-dots-vibrant-orange justify-center items-center">
              <p>Error updating channels and roles! Please contact support.</p>
            </div>
          )}
          {isRefreshButtonClicked &&
          !showSuccessUpdateMsg &&
          !showErrorUpdateMsg ? (
            <div className="flex justify-center items-center">
              <Spin />
            </div>
          ) : null}

          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, onBlur, value } }) => (
              <FormLabel label="Trigger name">
                <Input
                  type="text"
                  style={{ width: "100%" }}
                  readOnly={readOnly}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                />
              </FormLabel>
            )}
          />

          <Button
            type="primary"
            htmlType="submit"
            disabled={isLoading || savingName}
            className="modal-button modal-button-text modal-button-link"
            style={{ margin: "30px 0 30px 0" }}
          >
            <div className="flex gap-4 justify-center w-full">
              {(isLoading || savingName) && <Spin />}
              <span>Save</span>
            </div>
          </Button>
        </form>
      </div>
    </Modal>
  );
}

/**
 * Renders a modal depending on the value of state.editor.visibleModalId
 *
 */
export function Modals({
  changeTriggerName,
}: {
  changeTriggerName: (rootId: string, newName: string) => void;
}) {
  const { state, dispatch } = useTriggerEditorStateDispatch();

  const onCancel = () => {
    dispatch({ type: "hide_modal" });
  };
  switch (state.editor.visibleModalId) {
    case "try-draft":
      return <TryDraftModal onCancel={onCancel} />;
    case "settings":
      return (
        <SettingsModal
          onCancel={onCancel}
          changeTriggerName={changeTriggerName}
        />
      );
    default:
      return null;
  }
}
